import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckIcon, MinusIcon } from 'lucide-react';
import { useAssessment, useInstrumentConfig } from '../contexts/AssessmentContext';
import { instruments } from '../data/instruments';
import { InstrumentId } from '../types/assessment';
const CAPTURED = ['Which documents you open, and when', 'Flags, corrections, and everything you write in them', 'Every prompt to the assistant, its response, and anything you paste in', 'Timestamps for the actions above', 'Your final submission and reflection'];
const NOT_CAPTURED = ['Webcam or video of any kind', 'Keystrokes outside the input fields on this page', 'Clipboard contents from outside this window', 'Attention, gaze, or focus tracking', 'Anything at all after the session ends'];
export function Consent() {
  const {
    giveConsent,
    participantId: existingId,
    setInstrument
  } = useAssessment();
  const [participantId, setParticipantId] = useState(existingId);
  const [agreed, setAgreed] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const config = useInstrumentConfig();

  // The invite link carries the instrument, e.g. /consent?i=LAW-001.
  const requested = params.get('i')?.toUpperCase();
  useEffect(() => {
    if (requested && requested in instruments) setInstrument(requested as InstrumentId);
  }, [requested, setInstrument]);
  const canContinue = participantId.trim().length >= 3 && agreed;
  return <main className="w-full flex-1 bg-slate-100 py-12" data-id="element-538" data-mp="vtzm0tmy0h7z">
      <div className="mx-auto max-w-3xl px-6" data-id="element-539" data-mp="htf5k44i2xhn">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-540" data-mp="huqesjgpsxg5">
          Before we begin · {config.id} · {config.name}
        </p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900" data-id="element-541" data-mp="2c7pt899czllu">
          What this session records, and what it does not
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-slate-600" data-id="element-542" data-mp="2h5rl9joriv9g">
          This is a research session. Please read this page in full — you can stop at any point, and
          you can ask for your record to be deleted afterwards.
        </p>

        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6" data-id="element-543" data-mp="25qo7uodfplkg">
          <label htmlFor="pid" className="block text-sm font-medium text-slate-900" data-id="element-544" data-mp="20s4n2rakufto">
            Participant ID
          </label>
          <p className="mt-1 text-xs text-slate-500" data-id="element-545" data-mp="2yg4zvkx570g5">
            Use the code from your invitation. Your name stays out of the assessment record by design.
          </p>
          <input id="pid" value={participantId} onChange={e => setParticipantId(e.target.value.toUpperCase())} placeholder="e.g. P-014" className="mt-3 w-56 rounded-md border border-slate-300 px-3 py-2 font-mono-num text-sm uppercase tracking-wide text-slate-900 placeholder:normal-case placeholder:tracking-normal placeholder:text-slate-400 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200" data-id="element-546" data-mp="2oq26hjm3zkha" />
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2" data-id="element-547" data-mp="3opm4zb70qnbd">
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-548" data-mp="3oq5nou4htfqg">
            <h2 className="text-sm font-semibold text-slate-900" data-id="element-549" data-mp="3to78bjhkhn41">Captured</h2>
            <ul className="mt-3 space-y-2.5" data-id="element-550" data-mp="2ixpcbrzq93x">
              {CAPTURED.map(c => <li key={c} className="flex items-start gap-2.5 text-[13.5px] leading-snug text-slate-700" data-id="element-551" data-mp="7i0m78xth90t">
                  <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal-600" data-id="element-552" data-mp="biua42b9entp" />
                  {c}
                </li>)}
            </ul>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-553" data-mp="l95m8c53k3bo">
            <h2 className="text-sm font-semibold text-slate-900" data-id="element-554" data-mp="lb9okpncku9u">Not captured</h2>
            <ul className="mt-3 space-y-2.5" data-id="element-555" data-mp="q9te1015go6y">
              {NOT_CAPTURED.map(c => <li key={c} className="flex items-start gap-2.5 text-[13.5px] leading-snug text-slate-700" data-id="element-556" data-mp="v84o9bvcx0pp">
                  <MinusIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" data-id="element-557" data-mp="1rx5fkc65acze" />
                  {c}
                </li>)}
            </ul>
          </div>
        </div>

        <div className="mt-6 rounded-xl border-l-4 border-slate-900 bg-white p-6" data-id="element-558" data-mp="21n86m6tphgv3">
          <p className="text-[15px] font-medium leading-relaxed text-slate-900" data-id="element-559" data-mp="1wqhprha2uh8m">
            A named human reviews the full record before any conclusion is drawn. Nothing is decided
            by the system.
          </p>
        </div>

        <div className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-white p-6 text-[13.5px] leading-relaxed text-slate-600" data-id="element-560" data-mp="142lywt76vjjr">
          <p data-id="element-561" data-mp="1dz7013g38aib">
            <span className="font-semibold text-slate-900" data-id="element-562" data-mp="1dyp8ul8a8mh9">Retention and withdrawal. </span>
            Your record is held for twelve months, linked only to your participant ID. You can
            withdraw it at any time before or after the session by quoting that ID — no reason
            needed, and no consequence.
          </p>
          <p data-id="element-563" data-mp="2ww20wohxemw2">
            <span className="font-semibold text-slate-900" data-id="element-564" data-mp="2wzpsvj7q318o">Accommodations. </span>
            Extended timing and reduced-telemetry modes are available — contact us before starting.
          </p>
        </div>

        <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-xl border border-slate-300 bg-white p-5" data-id="element-565" data-mp="2n1tjowycigd7">
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400" data-id="element-566" data-mp="3foebfyiqguha" />
          <span className="text-sm text-slate-800" data-id="element-567" data-mp="35xjkwhygf8q1">
            I have read the above and consent to taking part in this session on these terms
          </span>
        </label>

        <button type="button" disabled={!canContinue} onClick={() => {
        giveConsent(participantId);
        navigate('/briefing');
      }} className="mt-6 rounded-lg bg-slate-900 px-6 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-300" data-id="element-568" data-mp="2hci0dnv5fib">
          Continue
        </button>
      </div>
    </main>;
}